import { GetRangeFromStringListByListWithMinAndMax } from '@operations/utils/get-range-from-list'
import { extractSubFormData, FieldStore, ISubFormStore, validateSubForm } from '@tom-ui/utils'
import { action, computed, makeObservable, observable } from 'mobx'
import {
  IYardBlockAssignment,
  IYardBlockAssignmentFormData,
} from '../models/operational-equipment-assignment.model'

export class YardBlockAssignmentSubFormStore
  implements ISubFormStore<IYardBlockAssignmentFormData>
{
  assignments = new FieldStore<(IYardBlockAssignment | null)[]>([])
  workAreaIds = new FieldStore<number[]>([])

  isForMultipleEquipment = false

  constructor() {
    makeObservable(this, {
      isForMultipleEquipment: observable,

      canAddAssignment: computed,
      displayContent: computed,
      duplicatedAssignments: computed,

      setValues: action,
    })
  }

  setValues(assignments: IYardBlockAssignment[], isForMultipleEquipment: boolean) {
    this.isForMultipleEquipment = isForMultipleEquipment
    if (!isForMultipleEquipment) this.assignments.setValue(assignments)

    const uniqueWorkAreaIds = assignments.reduce((acc: number[], assignment) => {
      if (assignment.workAreaId && !acc.includes(assignment.workAreaId)) {
        acc.push(assignment.workAreaId)
      }

      return acc
    }, [])

    this.workAreaIds.setValue(uniqueWorkAreaIds)
  }

  addAssignment() {
    const newAssignments = [...this.assignments.value]

    newAssignments.push({
      filter: undefined,
      workAreaId: null,
      bays: [],
      assignedBays: [],
      isDeleted: false,
      blockName: undefined,
    })

    this.assignments.setValue(newAssignments)
  }

  updateAssignment(
    index: number,
    workAreaId?: number | null,
    bays?: string[],
    assignedBays: string[] = [],
    blockName: string | undefined = undefined,
  ) {
    const newAssignments = [...this.assignments.value]
    newAssignments[index] = {
      filter: undefined,
      workAreaId: workAreaId,
      bays: bays,
      assignedBays: assignedBays,
      isDeleted: false,
      blockName: blockName,
    }

    this.assignments.setValue(newAssignments)
  }

  updateBays(index: number, availableBays?: string[], assignedBays?: string) {
    const newAssignments = [...this.assignments.value]

    const rangeOfSelectedBays = GetRangeFromStringListByListWithMinAndMax(
      availableBays ?? [],
      assignedBays?.split('-') ?? [],
    )

    const assignment = newAssignments[index]
    if (assignment) {
      assignment.assignedBays = rangeOfSelectedBays
      this.assignments.setValue(newAssignments)
    }
  }

  removeAssignment(index: number) {
    const newAssignments = [...this.assignments.value]

    const assignment = newAssignments[index]
    if (assignment) {
      assignment.isDeleted = true
      this.assignments.setValue(newAssignments)
    }
  }

  clearWorkAreas() {
    this.workAreaIds.setValue([])
  }

  validate = () => {
    return validateSubForm(this)
  }

  get containAssignments() {
    return this.assignments.value.some(a => a && a.isDeleted !== true)
  }

  get canAddAssignment() {
    return (
      !this.containAssignments && (!this.isForMultipleEquipment || !this.workAreaIds.value.length)
    )
  }

  get displayContent() {
    return (
      this.containAssignments || (this.isForMultipleEquipment && this.workAreaIds.value.length > 0)
    )
  }

  get duplicatedAssignments() {
    const seen = new Set<number>()
    const duplicates = new Set<string>()

    for (const assignment of this.assignments.value) {
      if (!assignment || assignment.isDeleted || assignment.workAreaId == null) continue

      if (!seen.has(assignment.workAreaId)) {
        seen.add(assignment.workAreaId)
      } else if (assignment.blockName) {
        duplicates.add(assignment.blockName)
      }
    }

    return Array.from(duplicates)
  }

  get data() {
    return extractSubFormData(this) as IYardBlockAssignmentFormData
  }
}
